/**
 * FERP Portal - Analytics List Page (DRR-074: Tabulator DRY Template)
 *
 * OPTIMIZE Phase: Workflow analytics and insights.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 * Sprint 3a (DRR-125): BarChart of currentValue by metricCategory from real backend data.
 * Sprint 3b (DRR-125): ExecutionAnalyticsDashboard wired in via second tab.
 */

import { useState, useMemo } from 'react';
import { Tabs, Paper, Stack, Title, Text, Center, Badge, Group, SimpleGrid, Loader } from '@mantine/core';
import { IconList, IconChartLine, IconAlertTriangle, IconDatabase } from '@tabler/icons-react';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { ExecutionAnalyticsDashboard } from '@ferp/workflow-forms';
import { usePrimitiveList } from '@/hooks';
import {
  MetricCategory,
  useGetAnalyticsManifestStatusQuery,
  useGetAnalyticsWithAnomaliesQuery,
  useGetAnalyticsWithThresholdBreachesQuery,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsTimeSeriesQuery,
} from '@/generated/graphql';
import { useTenantId } from '@/auth/AuthContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const CATEGORY_LABELS: Record<string, string> = {
  [MetricCategory.ADOPTION]: 'Adoption',
  [MetricCategory.BUSINESS]: 'Business',
  [MetricCategory.COMPLIANCE]: 'Compliance',
  [MetricCategory.PERFORMANCE]: 'Performance',
  [MetricCategory.SYSTEM]: 'System',
};

/** Analytics Manifest Status Summary */
function AnalyticsManifestStatus() {
  const { data, loading } = useGetAnalyticsManifestStatusQuery();

  if (loading) return <Center h={80}><Loader size="sm" /></Center>;

  const status = data?.analyticsManifestStatus;
  if (!status) return null;

  return (
    <Paper p="md" withBorder radius="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>Analytics Manifest</Title>
        <Badge color={status.status === 'READY' ? 'green' : 'yellow'} leftSection={<IconDatabase size={12} />}>
          {status.status}
        </Badge>
      </Group>
      <SimpleGrid cols={3}>
        <Text size="sm"><strong>Files:</strong> {status.filesIndexed}</Text>
        <Text size="sm"><strong>Rows:</strong> {status.totalRowsIndexed}</Text>
        <Text size="sm"><strong>Version:</strong> {status.version ?? 'N/A'}</Text>
      </SimpleGrid>
    </Paper>
  );
}

/** Analytics with Anomalies or Threshold Breaches */
function AnalyticsAlerts() {
  const tenantId = useTenantId();
  const { data: anomaliesData, loading: anomaliesLoading } = useGetAnalyticsWithAnomaliesQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });
  const { data: breachesData, loading: breachesLoading } = useGetAnalyticsWithThresholdBreachesQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });

  const loading = anomaliesLoading || breachesLoading;
  const anomalyCount = anomaliesData?.getAnalyticsWithAnomalies?.length ?? 0;
  const breachCount = breachesData?.getAnalyticsWithThresholdBreaches?.length ?? 0;

  if (loading) return <Center h={60}><Loader size="sm" /></Center>;

  return (
    <Paper p="md" withBorder radius="md">
      <Title order={5} mb="xs">Analytics Alerts</Title>
      <Group gap="lg">
        <Group gap="xs">
          <IconAlertTriangle size={16} color={anomalyCount > 0 ? 'orange' : 'gray'} />
          <Text size="sm">{anomalyCount} anomalies detected</Text>
        </Group>
        <Group gap="xs">
          <Badge color={breachCount > 0 ? 'red' : 'gray'} size="sm">
            {breachCount} threshold breaches
          </Badge>
        </Group>
      </Group>
    </Paper>
  );
}

function AnalyticsCategoryChart() {
  const { items, loading } = usePrimitiveList('analytics');

  const chartData = useMemo(() => {
    if (!items.length) return [];
    const totals: Record<string, number> = {};
    for (const item of items) {
      const cat: string = (item.metricCategory as string) ?? 'UNKNOWN';
      totals[cat] = (totals[cat] ?? 0) + ((item.currentValue as number) ?? 0);
    }
    return Object.entries(totals).map(([category, total]) => ({
      category: CATEGORY_LABELS[category] ?? category,
      value: Math.round(total * 100) / 100,
    }));
  }, [items]);

  return (
    <Paper p="md" withBorder radius="md">
      <Title order={5} mb="xs">
        Current Value by Metric Category
      </Title>
      {!loading && chartData.length === 0 ? (
        <Center h={160}>
          <Text c="dimmed" size="sm">
            No analytics data available
          </Text>
        </Center>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <XAxis dataKey="category" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Total Value']} />
            <Bar dataKey="value" fill="#4dabf7" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

export function AnalyticsListPage() {
  const [activeTab, setActiveTab] = useState<string | null>('records');
  const tenantId = useTenantId();

  // DRR-125: Analytics summary for dashboard overview
  const { data: summaryData } = useGetAnalyticsSummaryQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });

  // DRR-125: Time series data for trend visualization (placeholder policyId - will be dynamic)
  // This hook is wired but usage requires policy selection UI in future sprint
  const { data: timeSeriesData } = useGetAnalyticsTimeSeriesQuery({
    variables: {
      tenantId: tenantId ?? '',
      policyId: '', // Will be set when user selects a policy
      timeRange: 'LAST_30_DAYS',
    },
    skip: true, // Skip until policy selection is implemented
  });

  // Expose for future dashboard enhancements
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _analyticsData = { summary: summaryData, timeSeries: timeSeriesData };

  return (
    <Tabs value={activeTab} onChange={setActiveTab}>
      <Tabs.List>
        <Tabs.Tab value="records" leftSection={<IconList size={16} />}>
          Analytics Records
        </Tabs.Tab>
        <Tabs.Tab value="execution" leftSection={<IconChartLine size={16} />}>
          Execution Analytics
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="records" pt="md">
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <AnalyticsManifestStatus />
            <AnalyticsAlerts />
          </SimpleGrid>
          <AnalyticsCategoryChart />
          <TabulatorPrimitiveManager primitiveType="analytics" />
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="execution" pt="md">
        <ExecutionAnalyticsDashboard
          days={30}
          autoRefresh={true}
          showTrends={true}
          showBottlenecks={true}
          showFailures={true}
          showExport={true}
        />
      </Tabs.Panel>
    </Tabs>
  );
}

export default AnalyticsListPage;
